import React, { FC } from 'react';
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useLazyQuery, useQuery } from '@apollo/client';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { ProductionPlanningStep } from 'models/production-planning';
import { Order } from 'models/order';
import { planOverviewTemplate, getOrderDetailsTemplate } from 'apollo/templates/order';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import WorkflowActions from 'components/shared/workflow-actions/workflow-actions.component';
import { PlanOverviewResult } from './plan-overview-dialog.types';
import { buildDayGroups, formatDayGroupLabel } from './plan-overview-dialog.utils';
import PlanOverviewSpanCell from './plan-overview-span-cell.component';
import PlanOverviewLegend from './plan-overview-legend.component';

type PlanOverviewDialogProps = {
    open: boolean;
    onClose: () => void;
    steps?: ProductionPlanningStep[];
    orderId?: string;
    // When true (and opened for an order), embeds the workflow actions in a sticky
    // footer and highlights the order's current step row. Off for planning-only usages.
    withWorkflowActions?: boolean;
    onStepChanged?: () => void;
};

const PlanOverviewDialog: FC<PlanOverviewDialogProps> = ({
    open,
    onClose,
    steps,
    orderId,
    withWorkflowActions = false,
    onStepChanged,
}) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [fetchOverview, { data, loading }] = useLazyQuery<{ planOverview: PlanOverviewResult }>(
        planOverviewTemplate,
        { fetchPolicy: 'no-cache' },
    );

    // Order details power the workflow-actions footer and the current-step row highlight.
    // Read fresh on open, then from the cache, so a step change re-highlights instantly:
    // the workflow mutations return the Order and Apollo updates this view (DD-3231).
    const { data: orderData } = useQuery(getOrderDetailsTemplate, {
        variables: { orderId },
        skip: !open || !withWorkflowActions || !orderId,
        fetchPolicy: 'network-only',
        onError: (error) => showBoundary(error),
    });
    const order: Order | undefined = orderData?.order;

    const currentStepId = order?.workFlow?.currentStep?.stepId;

    React.useEffect(() => {
        if (!open) return;

        if (orderId) {
            fetchOverview({ variables: { orderId } });
            return;
        }

        const scheduledSteps = (steps || []).filter((s) => s.startDate && s.deadline);
        if (scheduledSteps.length === 0) return;

        fetchOverview({
            variables: {
                steps: scheduledSteps.map((s) => ({
                    stepId: s.stepId || '',
                    stepName: s.stepName,
                    startDate: s.startDate,
                    deadline: s.deadline,
                    estimatedProductionHours: s.estimatedProductionHours,
                    estimatedMachineHours: s.estimatedMachineHours,
                    machineId: s.machineId,
                    departmentId: s.departmentId,
                })),
            },
        });
    }, [open, steps, orderId, fetchOverview]);

    const result = data?.planOverview;
    const hourSlots = result?.hourSlots || [];
    const departmentGroups = result?.departmentGroups || [];
    const currentHourSlotIndex = result?.currentHourSlotIndex;

    const orderInfo = [result?.orderDisplayId, result?.clinicName, result?.patientName].filter(Boolean).join(', ');
    const dialogTitle = orderInfo
        ? `${translate('orders:PLAN_OVERVIEW')} - ${orderInfo}`
        : translate('orders:PLAN_OVERVIEW');

    const dayGroups = buildDayGroups(hourSlots);

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            fullWidth
            fullScreen
            maxWidth="xl"
            title={dialogTitle}
            sx={{
                // Make the dialog body fill the screen exactly so the table scrolls
                // internally and the workflow panel pins to the bottom (no whole-dialog scroll).
                '& .MuiDialog-paper > .MuiBox-root': {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
                '& .MuiDialogContent-root': {
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : hourSlots.length === 0 ? (
                <Typography color="text.secondary">{translate('orders:NO_STEPS_ADDED_YET')}</Typography>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        // Fill the dialog content area so the panel pins to the bottom and
                        // the table scrolls in the space above it (never hidden behind the panel).
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <PlanOverviewLegend />
                    <TableContainer
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            '&::-webkit-scrollbar': { height: 12, width: 12, WebkitAppearance: 'none' },
                            '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 6 },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#bdbdbd',
                                borderRadius: 6,
                                border: '2px solid #f1f1f1',
                            },
                            scrollbarWidth: 'auto',
                        }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        rowSpan={2}
                                        sx={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 3,
                                            backgroundColor: '#fff',
                                            minWidth: 200,
                                            borderRight: '1px solid #e0e0e0',
                                        }}
                                    >
                                        {translate('common:STEP')}
                                    </TableCell>
                                    {dayGroups.map((group, index) => (
                                        <TableCell
                                            key={`${group.dayLabel}-${index}`}
                                            colSpan={group.count}
                                            align="center"
                                            sx={{
                                                backgroundColor: '#f5f5f5',
                                                fontWeight: 600,
                                                borderLeft: '1px solid #e0e0e0',
                                            }}
                                        >
                                            {formatDayGroupLabel(group)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                <TableRow>
                                    {hourSlots.map((slot, index) => {
                                        const hasMachines = slot.machineRunNames && slot.machineRunNames.length > 0;
                                        const cell = (
                                            <TableCell
                                                key={index}
                                                align="center"
                                                sx={{
                                                    minWidth: 28,
                                                    fontSize: '0.65rem',
                                                    p: '2px 2px',
                                                    borderLeft: '1px solid #f0f0f0',
                                                    ...(index === currentHourSlotIndex && {
                                                        backgroundColor: '#fff9c4',
                                                    }),
                                                    ...(hasMachines &&
                                                        index !== currentHourSlotIndex && {
                                                            backgroundColor: '#e3f2fd',
                                                        }),
                                                }}
                                            >
                                                {slot.hour.toString().padStart(2, '0')}
                                            </TableCell>
                                        );
                                        return hasMachines ? (
                                            <Tooltip
                                                key={index}
                                                title={slot.machineRunNames!.join('\n')}
                                                slotProps={{
                                                    tooltip: { sx: { whiteSpace: 'pre-line' } },
                                                }}
                                                arrow
                                            >
                                                {cell}
                                            </Tooltip>
                                        ) : (
                                            cell
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {departmentGroups.map((group) => (
                                    <React.Fragment key={group.departmentName}>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    backgroundColor: '#fafafa',
                                                    fontWeight: 600,
                                                    fontSize: '0.8rem',
                                                    py: 0.5,
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 2,
                                                    borderRight: '1px solid #e0e0e0',
                                                }}
                                            >
                                                {group.departmentName}
                                            </TableCell>
                                            <TableCell
                                                colSpan={hourSlots.length}
                                                sx={{ backgroundColor: '#fafafa', py: 0.5 }}
                                            />
                                        </TableRow>
                                        {group.steps.map((span) => {
                                            const isCurrentStepRow = !!currentStepId && span.stepId === currentStepId;
                                            return (
                                                <TableRow key={span.stepId}>
                                                    <TableCell
                                                        sx={{
                                                            position: 'sticky',
                                                            left: 0,
                                                            zIndex: 2,
                                                            backgroundColor: isCurrentStepRow ? '#fff9c4' : '#fff',
                                                            borderRight: '1px solid #e0e0e0',
                                                            whiteSpace: 'nowrap',
                                                            fontSize: '0.8rem',
                                                        }}
                                                    >
                                                        {span.stepName}
                                                    </TableCell>
                                                    {hourSlots.map((_slot, slotIndex) => (
                                                        <PlanOverviewSpanCell
                                                            key={slotIndex}
                                                            slotIndex={slotIndex}
                                                            span={span}
                                                            hourSlots={hourSlots}
                                                            currentHourSlotIndex={currentHourSlotIndex}
                                                            isCurrentStepRow={isCurrentStepRow}
                                                        />
                                                    ))}
                                                </TableRow>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {withWorkflowActions && order && (
                        <Box
                            sx={{
                                flexShrink: 0,
                                backgroundColor: 'background.paper',
                                borderTop: '1px solid #e0e0e0',
                                mt: 1,
                                pt: 1.5,
                            }}
                        >
                            <WorkflowActions order={order} horizontal onCompleted={onStepChanged} />
                        </Box>
                    )}
                </Box>
            )}
        </DHDialog>
    );
};

export default PlanOverviewDialog;
